/**
 * useNotifications Hook
 *
 * Manages user notifications from Firestore.
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from '../../../services/firebase';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { useAuth } from '../../../contexts';

export type NotificationType =
  | 'booking_confirmed'
  | 'booking_cancelled'
  | 'booking_request'
  | 'message'
  | 'review'
  | 'payment'
  | 'reminder'
  | 'system';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  createdAt: Date;
  data?: {
    bookingId?: string;
    listingId?: string;
    threadId?: string;
  };
}

export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) {
        setNotifications([]);
        setIsLoading(false);
        return;
      }

      try {
        const notificationsRef = collection(db, 'notifications');
        const q = query(
          notificationsRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setNotifications([]);
          setIsLoading(false);
          return;
        }

        const notificationsData: Notification[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: data.type || 'system',
            title: data.title || '',
            message: data.message || '',
            read: data.read || false,
            createdAt: data.createdAt?.toDate() || new Date(),
            data: data.data,
          };
        });

        setNotifications(notificationsData);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setNotifications([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const notifRef = doc(db, 'notifications', notificationId);
      await updateDoc(notifRef, { read: true });

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user || notifications.length === 0) return;

    try {
      const batch = writeBatch(db);
      const unreadNotifs = notifications.filter(n => !n.read);

      unreadNotifs.forEach(notif => {
        const notifRef = doc(db, 'notifications', notif.id);
        batch.update(notifRef, { read: true });
      });

      await batch.commit();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [user, notifications]);

  const clearAll = useCallback(() => {
    // Just clear from local state - actual deletion would need Firestore
    setNotifications([]);
  }, []);

  return { notifications, unreadCount, isLoading, markAsRead, markAllAsRead, clearAll };
};

export default useNotifications;
